import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DialogBody, Text } from '@/ui';
import { Link } from '@/ui/primitives';
import type { ReactElement } from 'react';

type AppAnnouncementModalProps = {
  announcementKey: string;
  open: boolean;
  planUserLimit: number | null;
  onClose: () => void;
};

export const welcomePostOnboardingAnnouncementKey = 'welcome-post-onboarding';
export const teamInviteAfterFirstUploadAnnouncementKey = 'team-invite-after-first-upload';
export const secondUploadFeedbackAnnouncementKey = 'second-upload-feedback';

const workspaceUsersSettingsPath = '/workspace-settings?tab=users';
const workspaceUsersSettingsHref = import.meta.env.DEV
  ? workspaceUsersSettingsPath
  : `https://app.flowqa.com${workspaceUsersSettingsPath}`;

type RenderedAppAnnouncement = {
  title: string;
  content: ReactElement;
};

const formatInviteMemberLimit = (planUserLimit: number | null): string =>
  typeof planUserLimit === 'number' && planUserLimit > 1
    ? `up to ${String(planUserLimit - 1)} members`
    : 'members';

const renderAnnouncement = (
  announcementKey: string,
  planUserLimit: number | null,
): RenderedAppAnnouncement | null => {
  switch (announcementKey) {
    case welcomePostOnboardingAnnouncementKey:
      return {
        title: 'Welcome to FlowQA!',
        content: (
          <DialogBody>
            <Text role="body">
              FlowQA is currently in{' '}
              <Text role="bodyStrong" component="span">
                BETA
              </Text>
              . You may see rough edges while we continue to refine the product.
            </Text>
            <Text role="body" color="textSecondary">
              You're currently using the Free plan. Free plans are restricted by uploads, retention
              period, projects and users. Some features are also restricted.
            </Text>
            <Text role="body" color="textSecondary">
              If you're enjoying the app and need more, you can upgrade your plan at any time from
              your Workspace Settings.
            </Text>
            <Text role="body">
              To get started, upload a results file from your test run. FlowQA will turn this into
              meaningful execution runs, suites, tests, and quality trends you can track over time -
              getting more from your results.
            </Text>
            <Text role="body" color="textSecondary">
              Questions, feedback or feature requests? Use the Feedback link in the sidebar or email{' '}
              <Link href="mailto:support@flowqa.com">support@flowqa.com</Link> - we'd love to know
              what you think!
            </Text>
          </DialogBody>
        ),
      };
    case teamInviteAfterFirstUploadAnnouncementKey:
      return {
        title: 'Get More From Your Team Plan',
        content: (
          <DialogBody>
            <Text role="body">
              Team plans allow you to invite {formatInviteMemberLimit(planUserLimit)} of your team.
            </Text>
            <Text role="body" color="textSecondary">
              Open{' '}
              <Link href={workspaceUsersSettingsHref} target="_blank" rel="noopener noreferrer">
                Workspace Settings
              </Link>{' '}
              and add your team members when you're ready to collaborate.
            </Text>
          </DialogBody>
        ),
      };
    case secondUploadFeedbackAnnouncementKey:
      return {
        title: 'How is FlowQA fitting in?',
        content: (
          <DialogBody>
            <Text role="body">
              You have uploaded results more than once now, which is usually where FlowQA starts to
              become useful as a reporting habit.
            </Text>
            <Text role="body" color="textSecondary">
              If it helped, sharing it with one QA lead or engineering manager would be hugely
              useful. If it has not clicked yet, email{' '}
              <Link href="mailto:support@flowqa.com">support@flowqa.com</Link> with the rough edge
              you hit.
            </Text>
          </DialogBody>
        ),
      };
  }
  return null;
};

export const AppAnnouncementModal = ({
  announcementKey,
  open,
  planUserLimit,
  onClose,
}: AppAnnouncementModalProps): ReactElement | null => {
  const announcement = renderAnnouncement(announcementKey, planUserLimit);
  if (!announcement) return null;

  return (
    <ConfirmDialog
      open={open}
      title={announcement.title}
      confirmLabel="Got It"
      confirmColor="primary"
      confirmVariant="contained"
      hideCancel
      disableClose
      onClose={onClose}
      onConfirm={onClose}
    >
      {announcement.content}
    </ConfirmDialog>
  );
};
